/**
 * Componentes da tela nova do CCO (protótipo aprovado v4): cards, blocos, tendências, equipamento e diagnóstico —
 * Nexora Grid (GUS-1508/1504).
 *
 * Criado em: 18/10/2026 · Última modificação: 25/10/2026 · Versão: 1.4.0
 *
 * Os mesmos componentes servem à tela Clássica, à 3D (dentro da cena, pelo CSS3D) e à Apresentação.
 * - Card com barra lateral na cor da nota (ISA-101: cor + forma); normal em cinza-verde, sem dado em violeta hachurado.
 * - Todo card tem zoom: `data-zoom` + `data-href` (clique, Enter ou roda do mouse sobre o card aproximam — CcoNovo).
 * - Gráficos: só a linha estica com a largura; números dos eixos e o ponto final são HTML (o texto nunca estica).
 * - Equipamento (retorno de 18/10/2026): o painel do protótipo (última hora, valores, Diagnóstico) **somado** aos dados da
 *   tela antiga — medições com tendência, cadastro, manobra com confirmação (só na Clássica), alarmes e histórico do
 *   Grafana (clique abre em tela cheia). Diagnóstico do protótipo (fases × nominal, poço) somado à elétrica e mecânica.
 */
import { useEffect, useId, useState, type CSSProperties, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import { CortePoco } from "../cadastro/CortePoco";
import { pode } from "../permissoes";
import { useSessao } from "../sessao";
import { anomalo, type Nivel, type Nota } from "./anomalia";
import { useCco } from "./contexto";
import { TIPO_ICONE, TIPO_NOME, fmt, type Cidade, type Equipamento, type Indicadores, type Modelo, type SaudeConector, type Setor } from "./dados";
import { useHistoricoHora, useHistoricoOntem, type ComparacaoOntem, type Serie } from "./historico";
import { FORMA } from "./medidas";
import { Manobra } from "./Manobra";
import { LinkGrafana, PainelExpansivel, SeletorPeriodo } from "./PainelGrafana";
import { Valor } from "./Valor";

export const COR: Record<Nivel, string> = { normal: "var(--normal)", atencao: "var(--atencao)", alto: "var(--alto)", critico: "var(--critico)", sem_comunicacao: "var(--semcom)" };
export const ROTULO_NIVEL: Record<Nivel, string> = { normal: "", atencao: "● atenção", alto: "▲ alto", critico: "◆ crítico", sem_comunicacao: "⟂ sem dado" };
const cor = (n: Nota) => ({ "--cor": COR[n.nivel] }) as CSSProperties;

export const urlCidade = (c: string) => `/cco/cidade/${c}`;
export const urlSetor = (s: string) => `/cco/unidade/${s}`;
export const urlEquip = (d: string) => `/cco/equipamento/${d}`;
export const urlDiag = (d: string) => `/cco/equipamento/${d}/diagnostico`;

/** Medida principal do equipamento (gráfico do card e da última hora). */
export const PRINCIPAL: Record<string, [string, string]> = {
  reservatorio: ["nivel_reservatorio_pct", "%"], bomba: ["corrente_fase_a", "A"], vrp: ["pressao_jusante_mca", "mca"], macro: ["vazao_instantanea_m3h", "m³/h"],
};
// US-2306: Costa Rica mede a altura da água (m), a pressão de recalque do poço e a pressão na rua
const ALTERNATIVA: Record<string, [string, string]> = {
  reservatorio: ["nivel_m", "m"], poco: ["pressao_recalque_mca", "mca"], pressao: ["pressao_rede_mca", "mca"],
};
const principalDe = (e: Equipamento): [string, string] => {
  const p = PRINCIPAL[e.tipo];
  if (p && e.medidas.has(p[0])) return p;
  const alt = ALTERNATIVA[e.tipo];
  if (alt && e.medidas.has(alt[0])) return alt;
  return p ?? [[...e.medidas][0] ?? "", ""];
};

export const Kpi = ({ r, v, u = "" }: { r: string; v: ReactNode; u?: string }) => (
  <div className="kpi"><small>{r}</small><b>{v}{u && <i>{u}</i>}</b></div>
);

export function ChipNota({ nota, normal = "sem anomalia" }: { nota: Nota; normal?: string }) {
  return <span className="chip" style={cor(nota)}>{nota.nivel === "normal" ? normal : `${ROTULO_NIVEL[nota.nivel]} · ${nota.motivo}`}</span>;
}

// ---------------------------------------------------------------- gráficos (texto nunca estica)
const ultimaHora = (s: Serie) => s.filter((p) => p.t > Date.now() - 3600000);

export function Spark({ serie, alto = 30 }: { serie: Serie; alto?: number }) {
  const pts = ultimaHora(serie);
  if (pts.length < 2) return <div className="spark vazio" style={{ height: alto }} aria-hidden />;
  const vs = pts.map((p) => p.v), min = Math.min(...vs), max = Math.max(...vs), f = max - min || 1;
  const t0 = pts[0].t, t1 = pts[pts.length - 1].t || t0 + 1;
  const xy = pts.map((p) => [((p.t - t0) / (t1 - t0 || 1)) * 100, alto - 2 - ((p.v - min) / f) * (alto - 6)]);
  const linha = xy.map(([x, y]) => `${x.toFixed(2)},${y.toFixed(2)}`).join(" ");
  const [ux, uy] = xy[xy.length - 1];
  return (
    <div className="spark" style={{ height: alto }} aria-hidden>
      <svg viewBox={`0 0 100 ${alto}`} preserveAspectRatio="none"><polygon points={`0,${alto} ${linha} 100,${alto}`} className="area" />
        <polyline points={linha} className="linha" vectorEffect="non-scaling-stroke" /></svg>
      <span className="ponto-fim" style={{ left: `${ux}%`, top: `${(uy / alto) * 100}%` }} />
    </div>
  );
}

function SparkEquip({ e }: { e: Equipamento }) {
  const { empresa } = useCco();
  const serie = useHistoricoHora(empresa, e.device, principalDe(e)[0]);
  return <Spark serie={serie} />;
}

/** Gráfico da última hora do painel do equipamento (eixos em HTML). */
export function GraficoGrande({ serie, unidade, casas = 1, ontem }: { serie: Serie; unidade: string; casas?: number; ontem?: ComparacaoOntem }) {
  const [mostrarTendencia, setMostrarTendencia] = useState(true);
  const [compararOntem, setCompararOntem] = useState(true);
  const [selecionado, setSelecionado] = useState<number | null>(null);
  const detalheId = useId();
  const agora = Date.now();
  const pts = serie.filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v) && p.t > agora - 3600000 && p.t <= agora)
    .sort((a, b) => a.t - b.t);
  if (pts.length < 2) return <div className="grafico-caixa vazio">sem histórico na última hora</div>;
  const dia = 86400000;
  const anteriores = compararOntem && ontem?.estado === "pronto" ? ontem.serie
    .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.v) && p.t + dia > agora - 3600000 && p.t + dia <= agora)
    .sort((a, b) => a.t - b.t) : [];
  const L = 600, A = 150, vs = [...pts, ...anteriores].map((p) => p.v), min = Math.min(...vs), max = Math.max(...vs), pad = (max - min) * 0.15 || 1;
  const y = (v: number) => A - 6 - ((v - (min - pad)) / (max - min + 2 * pad)) * (A - 12);
  const t0 = agora - 3600000;
  const x = (t: number) => 46 + ((t - t0) / 3600000) * (L - 52);
  const linha = pts.map((p) => `${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join(" ");
  const marcas = [min, (min + max) / 2, max];
  const ultimo = pts[pts.length - 1];
  // Média móvel centrada de cinco minutos, somente sobre medições existentes; sem projeção.
  const tendencia = pts.map((p) => {
    const vizinhos = pts.filter((q) => Math.abs(q.t - p.t) <= 150000);
    return `${x(p.t).toFixed(1)},${y(vizinhos.reduce((s, q) => s + q.v, 0) / vizinhos.length).toFixed(1)}`;
  }).join(" ");
  const precisao = Math.min(6, Math.max(casas, max > min ? Math.ceil(-Math.log10((max - min) / 2)) : casas));
  const ativo = pts.find((p) => p.t === selecionado);
  const anteriorProximo = ativo ? anteriores.reduce<Serie[number] | null>((melhor, p) =>
    Math.abs(p.t + dia - ativo.t) <= 120000 && (!melhor || Math.abs(p.t + dia - ativo.t) < Math.abs(melhor.t + dia - ativo.t)) ? p : melhor, null) : null;
  // Lacunas acima de cinco minutos não são ligadas por uma linha de comparação.
  const linhaOntem = anteriores.map((p, i) => `${!i || p.t - anteriores[i - 1].t > 300000 ? "M" : "L"}${x(p.t + dia).toFixed(1)},${y(p.v).toFixed(1)}`).join(" ");
  const valor = (v: number) => `${fmt(v, Math.max(2, precisao))} ${unidade}`;
  const horario = (t: number) => new Date(t).toLocaleTimeString("pt-BR");
  return (
    <div className="grafico-medicoes">
      <div className="grafico-legenda">
        <span className="legenda-medida">● Medições · passe o mouse ou selecione um ponto</span>
        <label><input type="checkbox" checked={mostrarTendencia} onChange={(e) => setMostrarTendencia(e.target.checked)} />
          <span className="legenda-tendencia">┄ Tendência (média móvel de 5 min)</span></label>
        {ontem && <label><input type="checkbox" checked={compararOntem} onChange={(e) => setCompararOntem(e.target.checked)} />
          <span className="legenda-ontem" title="Referência com as medições do mesmo horário, 24 horas antes">┄ Tendência padrão</span></label>}
        {ontem && compararOntem && <span className="aviso" role="status">{ontem.estado === "carregando" ? "Carregando ontem…"
          : ontem.estado === "erro" ? "Não foi possível carregar o histórico de ontem"
          : anteriores.length < 2 ? "Sem histórico suficiente para a tendência padrão" : ""}</span>}
      </div>
    <div className="grafico-caixa" role="group" aria-label={`Medições da última hora: de ${valor(min)} a ${valor(max)}`}>
      <svg viewBox={`0 0 ${L} ${A}`} preserveAspectRatio="none" aria-hidden>
        {marcas.map((m, i) => <line key={i} x1="46" x2={L} y1={y(m)} y2={y(m)} className="grade-linha" vectorEffect="non-scaling-stroke" />)}
        <polygon points={`${x(pts[0].t)},${A} ${linha} ${x(ultimo.t)},${A}`} className="area" />
        <polyline points={linha} className="linha" vectorEffect="non-scaling-stroke" />
        {mostrarTendencia && <polyline points={tendencia} className="linha-tendencia" vectorEffect="non-scaling-stroke" />}
        {anteriores.length >= 2 && <path d={linhaOntem} className="linha-ontem" vectorEffect="non-scaling-stroke" />}
      </svg>
      {marcas.map((m, i) => <span key={i} className="eixo-y" style={{ top: `calc((100% - 16px) * ${(y(m) / A).toFixed(4)})` }}>{fmt(m, precisao)}</span>)}
      {pts.map((p, i) => <button key={`${p.t}-${i}`} type="button" className="ponto-medicao"
        style={{ left: `${x(p.t) / L * 100}%`, top: `calc((100% - 16px) * ${y(p.v) / A})` }}
        aria-label={`${horario(p.t)}: ${valor(p.v)}`} aria-describedby={ativo?.t === p.t ? detalheId : undefined}
        onMouseEnter={() => setSelecionado(p.t)} onMouseLeave={() => setSelecionado(null)}
        onFocus={() => setSelecionado(p.t)} onBlur={() => setSelecionado(null)} onClick={() => setSelecionado(p.t)} />)}
      {ativo && <output id={detalheId} className="medicao-detalhe"
        style={{ left: `${Math.min(85, Math.max(15, x(ativo.t) / L * 100))}%` }}>
        Hoje {horario(ativo.t)} · <strong>{valor(ativo.v)}</strong>
        {ontem && compararOntem && <span className="detalhe-ontem">{anteriorProximo
          ? `Ontem ${new Date(anteriorProximo.t).toLocaleDateString("pt-BR")} ${horario(anteriorProximo.t)} · ${valor(anteriorProximo.v)}`
          : "Ontem: sem medição próxima deste horário"}</span>}
      </output>}
      <span className="eixo-x" style={{ left: `${((46 / L) * 100).toFixed(2)}%` }}>−60 min</span><span className="eixo-x fim">agora</span>
    </div>
    <div className="grafico-ultima">Última medição: {horario(ultimo.t)} · <strong>{valor(ultimo.v)}</strong></div>
    </div>
  );
}

// ---------------------------------------------------------------- cards
type Zoom = { modelo: Modelo; copia?: boolean };

const ESTADO_FONTE: Record<SaudeConector["estado"], [string, string]> = { ok: ["API ok", "ok"], parcial: ["API parcial", "alerta"],
  atrasado: ["API atrasada", "falha"], falhando: ["API com falha", "falha"], sem_ciclo: ["API sem ciclo", "alerta"], pausado: ["API pausada", "pausada"] };

/** US-2004: selo da integração por API que alimenta a cidade (mesmo dado da tela do conector). */
export function SeloFonte({ fontes }: { fontes?: SaudeConector[] }) {
  if (!fontes?.length) return null;
  return <>{fontes.map((f) => {
    const [rotulo, classe] = ESTADO_FONTE[f.estado];
    const detalhe = [f.ciclo ? `último ciclo ${new Date(f.ciclo.em).toLocaleTimeString("pt-BR")} · ${f.ciclo.inseridos} ponto(s) novo(s)` : "sem ciclo",
      f.ciclo?.falhas_seguidas ? `${f.ciclo.falhas_seguidas} falha(s) seguida(s): ${f.ciclo.ultimo_erro ?? ""}` : "",
      f.sem_dado.length ? `sem dado: ${f.sem_dado.map((d) => d.device_id).join(", ")}` : ""].filter(Boolean).join(" · ");
    return <span key={f.nome} className={`selo-fonte ${classe}`} title={`${f.nome}: ${detalhe}`} aria-label={`${rotulo}: ${detalhe}`}>
      {rotulo}{f.estado === "parcial" ? ` · ${f.sem_dado.length} sem dado` : ""}</span>;
  })}</>;
}

export function CardCidade({ c, dica = "clique ou role para abrir a cidade ›" }: { c: Cidade; dica?: string }) {
  const { ind, nota, municipio: m } = c;
  return (
    <article className="card cidade" tabIndex={0} role="link" data-zoom="cidade" data-href={urlCidade(m.codigo)} data-cidade={m.codigo}
      data-nivel={nota.nivel} data-nota={nota.nota} style={cor(nota)} aria-label={`${m.nome}: ${anomalo(nota) ? nota.motivo : "sem anomalia"}. Abrir a cidade`}>
      <div className="topo"><h2>{m.nome}</h2><span className="nome">{m.uf} · {c.setores.length} setores{m.simulado && " · simulação"}</span>
        <SeloFonte fontes={m.fontes} /><span className="forma">{ROTULO_NIVEL[nota.nivel]}</span></div>
      <div className="kpis">
        <Kpi r="abastecimento" v={fmt(ind.abastecimento)} u="m³/h" /><Kpi r="reservação" v={fmt(ind.reservacao)} u="%" />
        <Kpi r="bombeamento" v={`${ind.ligadas}/${ind.bombas}`} /><Kpi r="pressão mín." v={fmt(ind.pressaoMin)} u="mca" />
        <Kpi r="alarmes 24h" v={ind.alarmes24} /><Kpi r="energia" v={fmt(ind.energia)} u="kW" />
      </div>
      <span className="zoom-dica">{dica}</span>
    </article>
  );
}

function SparkSetor({ s }: { s: Setor }) {
  const base = s.equipamentos.find((e) => e.tipo === "macro") ?? s.equipamentos.find((e) => e.tipo === "vrp")
    ?? s.equipamentos.find((e) => e.tipo === "reservatorio") ?? s.equipamentos[0];
  return base ? <SparkEquip e={base} /> : <div className="spark vazio" aria-hidden />;
}

export function CardSetor({ s, modelo, copia = false }: { s: Setor } & Zoom) {
  const { ind, nota } = s;
  const cidade = modelo.cidade(s.cidade);
  return (
    <article className={`card${copia ? " copia" : ""}`} tabIndex={copia ? -1 : 0} role="link" aria-hidden={copia || undefined}
      data-zoom="setor" data-href={urlSetor(s.codigo)} data-card={s.codigo} data-cidade={s.cidade} data-nivel={nota.nivel} data-nota={nota.nota}
      style={cor(nota)} aria-label={`Setor ${s.titulo}${cidade ? `, ${cidade.municipio.nome}` : ""}: ${anomalo(nota) ? nota.motivo : "normal"}`}>
      <div className="topo"><span className="id">{s.titulo}</span><span className="nome">{s.equipamentos.length} equipamentos</span>
        <span className="forma">{ROTULO_NIVEL[nota.nivel]}</span></div>
      <div className="kpis">
        <Kpi r="nível" v={ind.reservatorios.length ? fmt(ind.reservacao) : "—"} u="%" /><Kpi r="pressão" v={fmt(ind.pressaoMin)} u="mca" />
        <Kpi r="bombas" v={`${ind.ligadas}/${ind.bombas}`} /><Kpi r="vazão" v={fmt(ind.abastecimento || ind.produzida)} u="m³/h" />
        <Kpi r="energia" v={fmt(ind.energia, 1)} u="kW" /><Kpi r="alarmes" v={ind.alarmes24} u="24h" />
      </div>
      <SparkSetor s={s} />
      <div className="alerta">{anomalo(nota) ? nota.motivo : ""}</div>
    </article>
  );
}

/** Valores do equipamento por tipo (os mesmos do protótipo). */
export function linhaEq(e: Equipamento, valor: Modelo["valor"]): [string, string, string][] {
  const v = (m: string) => valor(e.device, m);
  if (e.tipo === "reservatorio") {
    if (v("nivel_reservatorio_pct") !== undefined) return [["nível", fmt(v("nivel_reservatorio_pct")), "%"], ["entrada", fmt(v("vazao_entrada_m3h")), "m³/h"], ["saída", fmt(v("vazao_saida_m3h")), "m³/h"]];
    // P-027: reservatório sem nível em % (Costa Rica: altura da água em metros, vazão e volume pelos pulsos)
    return [["nível", fmt(v("nivel_m"), 2), "m"], ["vazão", fmt(v("vazao_instantanea_m3h")), "m³/h"],
      ["volume", fmt(v("volume_total_acumulado_m3")), "m³"]];
  }
  if (e.tipo === "poco") return [["recalque", fmt(v("pressao_recalque_mca"), 1), "mca"]];
  if (e.tipo === "pressao") return [["pressão", fmt(v("pressao_rede_mca"), 1), "mca"]];
  if (e.tipo === "bomba") {
    const ligada = (v("bomba_ligada") ?? ((v("potencia_ativa_kw") ?? 0) > 0.5 ? 1 : 0)) >= 0.5;
    return [["estado", ligada ? "ligada" : "parada", ""], ["corrente", fmt(v("corrente_fase_a"), 1), "A"], ["vazão", fmt(v("vazao_instantanea_m3h")), "m³/h"],
      ["potência", fmt(v("potencia_ativa_kw"), 1), "kW"], ["ND", fmt(v("nivel_dinamico_m")), "m"], ["FP", fmt(v("fator_potencia"), 2), ""]];
  }
  if (e.tipo === "vrp") return [["jusante", fmt(v("pressao_jusante_mca"), 1), "mca"], ["montante", fmt(v("pressao_montante_mca")), "mca"], ["vazão", fmt(v("vazao_instantanea_m3h")), "m³/h"]];
  if (e.tipo === "macro") return [["vazão", fmt(v("vazao_instantanea_m3h")), "m³/h"], ["pressão", fmt(v("pressao_rede_mca"), 1), "mca"], ["volume", fmt(v("volume_total_acumulado_m3")), "m³"]];
  return [...e.medidas].slice(0, 3).map((m) => [m.replace(/_/g, " "), fmt(v(m), 1), ""]);
}

export function CardEquip({ e, modelo }: { e: Equipamento } & Zoom) {
  return (
    <article className="card" tabIndex={0} role="link" data-zoom="equip" data-href={urlEquip(e.device)} data-card={e.device} data-equip={e.device}
      data-nivel={e.nota.nivel} data-nota={e.nota.nota} style={cor(e.nota)} aria-label={`${TIPO_NOME[e.tipo]} ${e.nome}: ${anomalo(e.nota) ? e.nota.motivo : "normal"}`}>
      <div className="topo"><span className="id">{TIPO_ICONE[e.tipo]} {e.nome}</span><span className="nome">{TIPO_NOME[e.tipo]}</span>
        <span className="forma">{ROTULO_NIVEL[e.nota.nivel]}</span></div>
      <div className="kpis">{linhaEq(e, modelo.valor).map(([r, v, u]) => <Kpi key={r} r={r} v={v} u={u} />)}</div>
      <SparkEquip e={e} />
      <div className="alerta">{anomalo(e.nota) ? e.nota.motivo : ""}</div>
    </article>
  );
}

// ---------------------------------------------------------------- blocos
export function Hud({ ind, titulo }: { ind: Indicadores; titulo: string }) {
  const primeiro = ind.ativos[0];
  return (
    <section className="hud" aria-label={titulo}>
      <div className="bloco"><h3>Abastecimento</h3><span className="grande">{fmt(ind.abastecimento)}<i>m³/h</i></span><span className="sub">soma das vazões medidas agora</span></div>
      <div className="bloco"><h3>Reservação</h3><span className="grande">{fmt(ind.reservacao)}<i>%</i></span><span className="sub">média de {ind.reservatorios.length} reservatórios</span></div>
      <div className="bloco"><h3>Bombeamento</h3><span className="grande">{ind.ligadas}<i>de {ind.bombas}</i></span><span className="sub">bombas submersas em operação</span></div>
      <div className="bloco"><h3>Pressão</h3><span className="grande">{fmt(ind.pressaoMin, 1)}<i>mca</i></span><span className="sub">mínima · NBR 12218 ≥ 10 mca</span></div>
      <div className="bloco"><h3>Alarmes (24 h)</h3><span className="grande">{ind.ativos.length}<i>ativos · {ind.alarmes24} no dia</i></span>
        <span className="sub">{primeiro ? `${ROTULO_NIVEL[primeiro.nota.nivel].split(" ")[0]} ${primeiro.setor.toUpperCase()} · ${primeiro.nome}` : "nenhum ativo"}</span></div>
      <div className="bloco"><h3>Energia</h3><span className="grande">{fmt(ind.energia, 1)}<i>kW agora</i></span><span className="sub">{fmt(ind.kwhm3, 3)} kWh/m³ instantâneo</span></div>
    </section>
  );
}

/** Os seis blocos do nível da cidade (com as listas do protótipo). */
export function blocosCidade(c: Cidade): ReactNode[] {
  const ind = c.ind;
  const corEquip = (n: Nota) => (n.nivel === "normal" ? "var(--equipamento, var(--suave))" : COR[n.nivel]);
  return [
    <div className="bloco" key="a"><h3>Abastecimento</h3><span className="grande">{fmt(ind.abastecimento)}<i>m³/h</i></span><span className="sub">distribuídos agora</span>
      <span className="sub">produzidos {fmt(ind.produzida)} m³/h</span></div>,
    <div className="bloco" key="r"><h3>Reservação</h3><div className="lista-mini">{ind.reservatorios.slice(0, 5).map(({ e, nivel }) => (
      <div key={e.device}><span>{e.nome}</span><span className="barrinha"><i style={{ width: `${Math.min(100, nivel)}%`, "--equip": corEquip(e.nota) } as CSSProperties} /></span>
        <b>{fmt(nivel)}%</b></div>))}{!ind.reservatorios.length && <span className="sub">sem reservatório medido</span>}</div></div>,
    <div className="bloco" key="b"><h3>Bombeamento</h3><span className="grande">{ind.ligadas}<i>de {ind.bombas}</i></span><span className="sub">em operação</span>
      <span className="sub">{fmt(ind.produzida)} m³/h bombeados</span></div>,
    <div className="bloco" key="p"><h3>Pressão</h3><div className="lista-mini">{ind.pressoes.slice(0, 4).map(({ setor, p }) => (
      <div key={setor}><span>{setor}</span><span /><b>{fmt(p, 1)}</b></div>))}</div><span className="sub">mca · NBR 12218 ≥ 10</span></div>,
    <div className="bloco" key="al"><h3>Alarmes (24 h)</h3><span className="grande">{ind.ativos.length}<i>ativos</i></span><div className="lista-mini">
      {ind.ativos.slice(0, 3).map((e) => <div key={e.device} className="uma"><span style={{ color: COR[e.nota.nivel] }}>{ROTULO_NIVEL[e.nota.nivel].split(" ")[0]} {e.setor.toUpperCase()} · {e.nota.motivo}</span></div>)}
      {!ind.ativos.length && <span className="sub">nenhum ativo</span>}</div></div>,
    <div className="bloco" key="e"><h3>Energia</h3><span className="grande">{fmt(ind.energia, 1)}<i>kW</i></span><span className="sub">{fmt(ind.kwhm3, 3)} kWh/m³</span></div>,
  ];
}

type Tendencia = Record<"nivel" | "vazao" | "potencia" | "pressao", [string, number][]>;
const TENDENCIAS: [keyof Tendencia, string, string][] = [["nivel", "Nível médio · 24 h", "%"], ["vazao", "Vazão distribuída · 24 h", "m³/h"],
  ["potencia", "Potência das bombas · 24 h", "kW"], ["pressao", "Pressão mínima · 24 h", "mca"]];

/** As quatro tendências de 24 h da cidade (hora a hora, do hub). */
export function useTendencias(cidade?: string): Tendencia | null {
  const { empresa } = useCco();
  const [dados, setDados] = useState<Tendencia | null>(null);
  useEffect(() => {
    if (!empresa || !cidade) return;
    let vivo = true;
    const carregar = () => api<{ series: Tendencia }>(`/v1/tempo-real/tendencias?empresa=${empresa}&municipio=${cidade}&horas=24`)
      .then((r) => { if (vivo) setDados(r.series); }).catch(() => undefined);
    void carregar();
    const relogio = window.setInterval(carregar, 300000);
    return () => { vivo = false; window.clearInterval(relogio); };
  }, [empresa, cidade]);
  return dados;
}

export function blocosTendencia(t: Tendencia | null): ReactNode[] {
  return TENDENCIAS.map(([k, titulo, u]) => {
    const pts = (t?.[k] ?? []).map(([iso, v]) => ({ t: Date.parse(iso), v }));
    const vs = pts.map((p) => p.v);
    // o Spark usa só a última hora; aqui a série é de 24 h: redistribui no tempo para caber na mesma janela
    const agora = Date.now();
    const comprimida = pts.map((p, i) => ({ t: agora - 3500000 + (i / Math.max(1, pts.length - 1)) * 3400000, v: p.v }));
    return (
      <div className="bloco" key={k}><h3>{titulo}</h3><span className="sub">{vs.length ? `${fmt(Math.min(...vs))}–${fmt(Math.max(...vs))} ${u}` : "sem dados"}</span>
        <Spark serie={comprimida} alto={40} /></div>
    );
  });
}

// ---------------------------------------------------------------- equipamento e diagnóstico
export function PainelEquip({ e, modelo, nos3d = false }: { e: Equipamento; modelo: Modelo; nos3d?: boolean }) {
  const { empresa } = useCco();
  const [medida, unidade] = principalDe(e);
  const serie = useHistoricoHora(empresa, e.device, medida);
  const ontem = useHistoricoOntem(empresa, e.device, medida);
  return (
    <div className={`painel${nos3d ? " p3d" : ""}`} data-equip-painel={e.device}>
      <h3>{TIPO_ICONE[e.tipo]} {e.nome} · última hora ({unidade})</h3>
      <GraficoGrande serie={serie} unidade={unidade} casas={e.tipo === "bomba" || e.tipo === "vrp" ? 1 : 0} ontem={ontem} />
      <div className="valores">{linhaEq(e, modelo.valor).map(([r, v, u]) => <Kpi key={r} r={r} v={v} u={u} />)}</div>
      <div className="acoes-nivel">
        <button type="button" className="botao" data-zoom="diag" data-href={urlDiag(e.device)} data-equip={e.device}>Diagnóstico ›</button>
        <span className="aviso">{e.setor.toUpperCase()} · {nos3d ? "manobra só na tela Clássica do CCO, por usuário autorizado" : "manobra abaixo, com confirmação"}</span>
      </div>
    </div>
  );
}

const MEDIDAS_N3 = ["nivel_reservatorio_pct", "nivel_m", "vazao_instantanea_m3h", "vazao_entrada_m3h", "vazao_saida_m3h", "pressao_rede_mca",
  "pressao_jusante_mca", "pressao_montante_mca", "pressao_recalque_mca", "pressao_mca",
  "pressao_recalque_bar", "corrente_fase_a", "tensao_fase_v",
  "potencia_ativa_kw", "frequencia_inversor_hz", "temperatura_bomba_c", "volume_total_acumulado_m3", "nivel_dinamico_m"];
// Painéis do dashboard "CCO · Equipamento" (infra/grafana/dashboards/cco_equipamento.json) por medida
const PAINEL_DA_MEDIDA: Record<string, [number, string]> = {
  nivel_reservatorio_pct: [1, "Nível do reservatório"], nivel_m: [2, "Altura da água"], vazao_instantanea_m3h: [3, "Vazão"],
  vazao_entrada_m3h: [3, "Vazão"], vazao_saida_m3h: [3, "Vazão"], pressao_rede_mca: [4, "Pressão"], pressao_jusante_mca: [4, "Pressão"],
  pressao_montante_mca: [4, "Pressão"], pressao_recalque_mca: [4, "Pressão"],
  pressao_mca: [4, "Pressão"], corrente_fase_a: [5, "Corrente"], potencia_ativa_kw: [6, "Potência ativa"],
  temperatura_bomba_c: [7, "Temperatura do motor"], tensao_fase_v: [8, "Tensão"], frequencia_inversor_hz: [9, "Frequência do inversor"],
  pressao_recalque_bar: [10, "Pressão de recalque"],
};

/** Os dados da tela antiga do equipamento (retorno de 18/10/2026): medições, cadastro, manobra, alarmes e histórico. */
export function DetalheEquip({ e }: { e: Equipamento }) {
  const { usuario } = useSessao();
  const { empresa, alarmes } = useCco();
  const [de, setDe] = useState("now-6h");
  const meus = alarmes.filter((a) => a.device_id === e.device);
  const paineis = [...new Map([...e.medidas].filter((m) => PAINEL_DA_MEDIDA[m]).map((m) => PAINEL_DA_MEDIDA[m]).map((p) => [p[0], p])).values()]
    .sort((a, b) => a[0] - b[0]);
  return (
    <>
      <div className="detalhe">
        <div className="painel">
          <h3>Medições <span className="aviso">tendência da última hora e faixa normal (ISA-101)</span></h3>
          <div className="grade-valores">{MEDIDAS_N3.filter((m) => e.medidas.has(m)).map((m) => <Valor key={m} device={e.device} medida={m} grande />)}</div>
        </div>
        <div className="coluna-detalhe">
          <div className="painel">
            <h3>Cadastro</h3>
            {e.ativos.length ? e.ativos.map((a) => (
              <p key={a.id}><Link to={`/cadastro/${a.id}`}>{a.dados.identificacao}</Link> · {a.dados.tipo} · setor {a.dados.setor || "—"} ·{" "}
                <span className={`nx-selo situacao-${a.situacao}`}>{a.situacao}</span>{a.pendencias_ficha.length > 0 && <span className="aviso"> · ficha incompleta</span>}</p>
            )) : <p className="aviso">dispositivo sem ativo ligado no cadastro</p>}
          </div>
          {usuario && pode.manobrar(usuario) && empresa && e.tipo === "bomba" && <Manobra empresa={empresa} device={e.device} nome={e.nome} />}
          {usuario && !pode.manobrar(usuario) && e.tipo === "bomba" && <p className="aviso">Manobra: só operador, supervisor ou coordenador da operadora.</p>}
          <div className="painel">
            <h3>Alarmes do equipamento (24 h)</h3>
            {meus.length ? (
              <ul className="lista-alarmes-curta">{meus.map((a) => <li key={a.tipo} className={`prioridade-${a.prioridade}`}>
                <span className="forma">{FORMA[a.prioridade]}</span> {a.tipo.replace(/_/g, " ").toLowerCase()} · {a.ocorrencias}×{a.reconhecido ? " · reconhecido" : ""}</li>)}</ul>
            ) : <p className="aviso">nenhum</p>}
          </div>
        </div>
      </div>
      {empresa && paineis.length > 0 && (
        <div className="painel historico-equip">
          <h3>Histórico <SeletorPeriodo valor={de} aoMudar={setDe} /><span className="aviso">clique num gráfico para tela cheia</span>
            <LinkGrafana empresa={empresa} uid="nexora-cco-equipamento" de={de} variaveis={{ dispositivo: e.device }}>abrir no Grafana ›</LinkGrafana></h3>
          <div className="grade-paineis">
            {paineis.map(([id, titulo]) => <PainelExpansivel key={id} empresa={empresa} uid="nexora-cco-equipamento" painel={id} titulo={titulo} de={de} variaveis={{ dispositivo: e.device }} />)}
          </div>
        </div>
      )}
    </>
  );
}

const MEDIDAS_N4 = ["corrente_fase_a", "tensao_fase_v", "potencia_ativa_kw", "frequencia_inversor_hz", "temperatura_bomba_c", "fator_potencia",
  "desequilibrio_corrente_pct", "desequilibrio_tensao_pct"];

/** Diagnóstico do protótipo: fases R/S/T contra a nominal, chips e o corte do poço (ficha ou parâmetros do CLP). */
export function CorpoDiag({ e, modelo, completo = false }: { e: Equipamento; modelo: Modelo; completo?: boolean }) {
  const { empresa } = useCco();
  const [medida, unidade] = principalDe(e);
  const serie = useHistoricoHora(empresa, e.device, medida);
  const ontem = useHistoricoOntem(empresa, e.device, medida);
  if (e.tipo !== "bomba") {
    return <><h3>Comportamento · {e.nome}</h3><GraficoGrande serie={serie} unidade={unidade} ontem={ontem} />
      <p className="aviso">O diagnóstico detalhado (fases e poço) é das bombas.</p></>;
  }
  const v = (m: string) => modelo.valor(e.device, m);
  const ficha = e.ativos[0]?.ficha ?? {};
  const nominal = Number(ficha.corrente_nominal_a) || v("corrente_nominal_a") || 0;
  const origemNominal = Number(ficha.corrente_nominal_a) ? "ficha" : "inversor";
  const fases = ["r", "s", "t"].map((f) => v(`corrente_fase_${f}_a`) ?? v("corrente_fase_a") ?? 0);
  const media = fases.reduce((a, b) => a + b, 0) / 3 || 1;
  const deseq = v("desequilibrio_corrente_pct") ?? (Math.max(...fases.map((f) => Math.abs(f - media))) / media) * 100;
  const pct = nominal ? (Math.max(...fases) / nominal) * 100 : 0;
  const escala = (nominal || Math.max(...fases, 1)) * 1.4;
  const num = (k: string, m: string) => Number(ficha[k]) || v(m) || undefined;
  const poco = { profundidade_poco_m: num("profundidade_poco_m", "profundidade_poco_m"), nivel_estatico_m: num("nivel_estatico_m", "nivel_estatico_m"),
    nivel_dinamico_m: num("nivel_dinamico_m", "nivel_dinamico_m"), profundidade_instalacao_m: num("profundidade_instalacao_m", "profundidade_bomba_m"),
    topo_filtros_m: Number(ficha.topo_filtros_m) || undefined };
  return (
    <>
      <h3>Diagnóstico · {e.nome}</h3>
      <div className="diag3d">
        <div>
          <div className="fases">{["R", "S", "T"].map((f, k) => (
            <div className="fase" key={f}><b>{f}</b>
              <div className="barra-f"><span style={{ width: `${Math.min(100, (fases[k] / escala) * 100)}%` }} />{nominal > 0 && <em style={{ left: `${100 / 1.4}%` }} title="nominal" />}</div>
              <span>{fmt(fases[k], 1)} A</span></div>))}</div>
          <div className="acoes-nivel">
            <span className="chip">{nominal ? `nominal ${fmt(nominal, 1)} A (${origemNominal})` : "sem corrente nominal"}</span>
            <span className="chip" style={{ "--cor": deseq > 10 ? "var(--alto)" : "var(--suave)" } as CSSProperties}>desequilíbrio {fmt(deseq, 1)}%</span>
            <span className="chip">FP {fmt(v("fator_potencia"), 2)}</span>
            {nominal > 0 && <span className="chip" style={{ "--cor": pct > 110 ? "var(--critico)" : "var(--suave)" } as CSSProperties}>{fmt(pct)}% da nominal</span>}
          </div>
        </div>
        <div className="corte"><CortePoco poco={poco} /></div>
      </div>
      {completo && (
        <>
          <h3 className="secao-titulo">Elétrica e mecânica</h3>
          <div className="grade-valores">{MEDIDAS_N4.filter((m) => e.medidas.has(m)).map((m) => <Valor key={m} device={e.device} medida={m} grande />)}</div>
        </>
      )}
    </>
  );
}
